"use client";

import { useState } from "react";
import { ArrowLeft, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { selectCompany, type CompanyOption } from "@/modules/auth/api";

interface CompanySelectorProps {
  companies: CompanyOption[];
  tempToken: string;
  firstName: string;
  email: string;
  onBack: () => void;
}

const roleBadge: Record<string, { label: string; className: string }> = {
  owner: { label: "Propietario", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  admin: { label: "Administrador", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  user: { label: "Usuario", className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" },
};

export function CompanySelector({ companies, tempToken, firstName, email, onBack }: CompanySelectorProps) {
  const [isSelecting, setIsSelecting] = useState<string | null>(null);

  const handleSelect = async (companyId: string) => {
    setIsSelecting(companyId);
    try {
      await selectCompany(companyId, tempToken);

      // Redirect al dashboard
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith("/app/")) {
          window.location.href = "/app/dashboard";
        } else {
          window.location.href = "/dashboard";
        }
      }
    } catch (error: unknown) {
      setIsSelecting(null);

      // Detectar si el temp token expiró (401)
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        toast.error("Tu sesión temporal expiró. Vuelve a iniciar sesión.");
        onBack();
        return;
      }

      toast.error("No se pudo seleccionar la empresa. Intenta de nuevo.");
    }
  };

  const displayName = firstName || email.split("@")[0];

  return (
    <div className="space-y-6">
      {/* Saludo */}
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold text-foreground md:text-2xl">
          Hola {displayName}, ¿desde dónde te conectas hoy?
        </h2>
        <p className="text-xs text-muted-foreground">
          Selecciona la empresa con la que deseas trabajar
        </p>
      </div>

      {/* Grid de empresas */}
      <div className="grid gap-3">
        {companies.map((company) => {
          const badge = roleBadge[company.role] || roleBadge.user;
          const isLoading = isSelecting === company.id;
          const isDisabled = isSelecting !== null;

          return (
            <button
              key={company.id}
              onClick={() => handleSelect(company.id)}
              disabled={isDisabled}
              className={`
                group relative w-full rounded-xl border border-border bg-card p-4
                text-left transition-all duration-200
                ${isDisabled && !isLoading ? "opacity-50 cursor-not-allowed" : ""}
                ${isLoading ? "ring-2 ring-primary border-primary" : "hover:border-primary/50 hover:shadow-md"}
              `}
            >
              <div className="flex items-center gap-4">
                {/* Logo o inicial */}
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-semibold text-lg">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="h-12 w-12 rounded-xl object-cover"
                    />
                  ) : (
                    <Building2 className="h-6 w-6" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{company.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
                      {badge.label}
                    </span>
                  </div>
                </div>

                {/* Loading indicator */}
                {isLoading && (
                  <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Botón volver */}
      <button
        onClick={onBack}
        disabled={isSelecting !== null}
        className="flex items-center gap-2 mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
      >
        <ArrowLeft className="h-3 w-3" />
        Volver al inicio de sesión
      </button>
    </div>
  );
}
